import React, { useState } from 'react';
import { 
  FileText, 
  Search, 
  Mic, 
  StopCircle, 
  Copy, 
  Download, 
  ArrowLeft,
  Clipboard,
  Stethoscope,
  Scissors,
  Image as ImageIcon,
  Beaker,
  Users,
  LogOut,
  Calendar,
  Shield,
  Pill,
  Microscope,
  GraduationCap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';

interface ReportTemplate {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  category: string;
}

const ReportGeneration: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [generatedContent, setGeneratedContent] = useState<string>('');
  
  const categories = [
    { id: 'all', name: t('reports.categories.all') },
    { id: 'clinical', name: t('reports.categories.clinical') },
    { id: 'diagnostic', name: t('reports.categories.diagnostic') },
    { id: 'surgical', name: t('reports.categories.surgical') },
    { id: 'consultation', name: t('reports.categories.consultation') },
    { id: 'followup', name: t('reports.categories.followup') },
    { id: 'prescription', name: t('reports.categories.prescription') },
    { id: 'administrative', name: t('reports.categories.administrative') },
    { id: 'education', name: t('reports.categories.education') }
  ];
  
  const reportTemplates: ReportTemplate[] = [
    { id: '1', title: t('reports.templates.outpatientRecord.title'), description: t('reports.templates.outpatientRecord.description'), icon: FileText, category: 'clinical' },
    { id: '2', title: t('reports.templates.inpatientRecord.title'), description: t('reports.templates.inpatientRecord.description'), icon: Stethoscope, category: 'clinical' },
    { id: '3', title: t('reports.templates.surgicalRecord.title'), description: t('reports.templates.surgicalRecord.description'), icon: Scissors, category: 'surgical' },
    { id: '4', title: t('reports.templates.imagingReport.title'), description: t('reports.templates.imagingReport.description'), icon: ImageIcon, category: 'diagnostic' },
    { id: '5', title: t('reports.templates.pathologyReport.title'), description: t('reports.templates.pathologyReport.description'), icon: Beaker, category: 'diagnostic' },
    { id: '6', title: t('reports.templates.consultationReport.title'), description: t('reports.templates.consultationReport.description'), icon: Users, category: 'consultation' },
    { id: '7', title: t('reports.templates.dischargeSummary.title'), description: t('reports.templates.dischargeSummary.description'), icon: LogOut, category: 'clinical' },
    { id: '8', title: t('reports.templates.followupRecord.title'), description: t('reports.templates.followupRecord.description'), icon: Calendar, category: 'followup' },
    { id: '9', title: t('reports.templates.medicalCertificate.title'), description: t('reports.templates.medicalCertificate.description'), icon: Shield, category: 'administrative' },
    { id: '10', title: t('reports.templates.prescription.title'), description: t('reports.templates.prescription.description'), icon: Pill, category: 'prescription' },
    { id: '11', title: t('reports.templates.labInterpretation.title'), description: t('reports.templates.labInterpretation.description'), icon: Microscope, category: 'diagnostic' },
    { id: '12', title: t('reports.templates.patientEducation.title'), description: t('reports.templates.patientEducation.description'), icon: GraduationCap, category: 'education' }
  ];
  
  const filteredTemplates = reportTemplates.filter(template => {
    const matchesCategory = activeCategory === 'all' || template.category === activeCategory;
    const matchesSearch = template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  
  const handleTemplateSelect = (template: ReportTemplate) => {
    setSelectedTemplate(template);
    setGeneratedContent('');
  };
  
  const handleBackToTemplates = () => {
    setSelectedTemplate(null);
    setGeneratedContent('');
    setIsRecording(false);
  };
  
  const handleToggleRecording = () => {
    if (isRecording) {
      setIsRecording(false);
      setTimeout(() => setGeneratedContent("ASR engine not connected..."), 1000);
    } else {
      setIsRecording(true);
      setRecordingTime(0);
      const timer = setInterval(() => setRecordingTime(p => p + 1), 1000);
      return () => clearInterval(timer);
    }
  };
  
  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('reports.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('reports.description')}</p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {selectedTemplate ? (
          <motion.div
            key="editor"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleBackToTemplates} className="rounded-xl">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('reports.backToTemplates')}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 rounded-2xl border-gray-200/60 shadow-sm overflow-hidden h-fit">
                <CardHeader className="bg-gray-50/50 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-xl">
                      <selectedTemplate.icon className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{selectedTemplate.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
                  <Separator />
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">{t('reports.dictation')}</h4>
                      {isRecording && (
                        <Badge variant="destructive" className="animate-pulse rounded-full">
                          {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                        </Badge>
                      )}
                    </div>
                    <div className={`aspect-video rounded-2xl border-2 border-dashed flex flex-col items-center justify-center p-6 text-center transition-all ${
                      isRecording ? 'border-destructive/40 bg-destructive/5' : 'border-gray-100 bg-gray-50/50'
                    }`}>
                      {isRecording ? (
                        <div className="space-y-2">
                          <div className="flex gap-1 justify-center">
                            {[1, 2, 3, 4, 5].map(i => (
                              <motion.div
                                key={i}
                                animate={{ height: [10, 25, 10] }}
                                transition={{ repeat: Infinity, duration: 0.5, delay: i * 0.1 }}
                                className="w-1 bg-destructive rounded-full"
                              />
                            ))}
                          </div>
                          <p className="text-xs text-destructive font-medium uppercase tracking-wider">{t('reports.recordingInProgress')}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">{t('reports.clickToStartRecording')}</p>
                      )}
                    </div>
                    <Button 
                      onClick={handleToggleRecording} 
                      className={`w-full rounded-xl h-12 shadow-md ${isRecording ? 'bg-destructive hover:bg-destructive/90' : 'bg-primary hover:bg-primary/90'}`}
                    >
                      {isRecording ? <StopCircle className="h-5 w-5 mr-2" /> : <Mic className="h-5 w-5 mr-2" />}
                      {isRecording ? t('reports.stopRecording') : t('reports.startRecording')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2 rounded-2xl border-gray-200/60 shadow-sm overflow-hidden bg-white min-h-[500px]">
                <CardHeader className="bg-white border-b border-gray-100 flex flex-row items-center justify-between p-6">
                  <CardTitle className="text-lg">{t('reports.generatedReport', { template: selectedTemplate.title })}</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-lg h-8">
                      <Copy className="h-3.5 w-3.5 mr-2" /> {t('reports.copy')}
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-lg h-8">
                      <Download className="h-3.5 w-3.5 mr-2" /> {t('reports.download')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <div className="p-8">
                      {generatedContent ? (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="prose prose-sm max-w-none text-gray-800 font-mono whitespace-pre-wrap leading-relaxed"
                        >
                          {generatedContent}
                        </motion.div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-[300px] text-gray-300">
                          <Clipboard className="h-16 w-16 mb-4 opacity-20" />
                          <p className="text-sm italic">{t('reports.noContentYet') || 'No report generated yet. Start recording to begin.'}</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="templates"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card className="rounded-2xl border-gray-200/60 shadow-sm overflow-hidden bg-white/50 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder={t('reports.searchPlaceholder')} 
                      className="pl-9 rounded-xl border-gray-200 bg-white"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                    {categories.slice(0, 5).map((category) => (
                      <Button
                        key={category.id}
                        variant={activeCategory === category.id ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setActiveCategory(category.id)}
                        className="rounded-full px-4 whitespace-nowrap"
                      >
                        {category.name}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredTemplates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => handleTemplateSelect(template)}
                  className="group relative cursor-pointer"
                >
                  <Card className="h-full rounded-2xl border-gray-100 hover:border-primary/40 transition-all hover:shadow-lg hover:shadow-primary/5 active:scale-95">
                    <CardContent className="p-5 flex flex-col items-center text-center">
                      <div className="h-12 w-12 bg-gray-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                        <template.icon className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors">{template.title}</h3>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{template.description}</p>
                      <div className="mt-4 pt-4 border-t border-gray-50 w-full flex justify-center">
                        <Badge variant="secondary" className="bg-primary/5 text-primary text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 border-none">
                          {t('reports.aiAssisted')}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReportGeneration;
